// VIBRANIAM-SPACE v2 - shaders.js
//
// Every offset here is in UV or in units proportional to the framebuffer, never
// in absolute pixels. That is what keeps a 960x540 preview and a 3840x2160
// export the same image at different sharpness.

export const PARTICLE_VERT = /* glsl */ `
attribute float aSize;
attribute float aAlpha;
varying vec3 vColor;
varying float vAlpha;
uniform float uViewportH;   // framebuffer height in pixels
uniform float uSizeScale;   // world-units -> screen factor

void main() {
  vColor = color;
  vAlpha = aAlpha;
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  float d = max(0.001, -mv.z);
  // Point size proportional to the viewport height: the same scene fills the
  // same fraction of the frame at any resolution. Point sprites below ~2px
  // rasterize unreliably on some GPU/driver combinations (observed: entire
  // fields silently vanishing, clamped at exactly 1.0px, on ANGLE/D3D11) -
  // the floor keeps every particle above that edge instead of flickering
  // in and out of existence per-pixel.
  gl_PointSize = clamp(aSize * uSizeScale * uViewportH / d, 2.0, 1024.0);
  gl_Position = projectionMatrix * mv;
}
`;

export const PARTICLE_FRAG = /* glsl */ `
precision highp float;
varying vec3 vColor;
varying float vAlpha;
uniform float uGain;
uniform float uCore;   // how tight the bright centre is

void main() {
  vec2 d = gl_PointCoord - vec2(0.5);
  float r = length(d) * 2.0;
  if (r > 1.0) discard;
  float falloff = pow(1.0 - r, 2.0);
  float core = pow(1.0 - r, uCore) * 0.6;
  float a = (falloff + core) * vAlpha;
  gl_FragColor = vec4(vColor * uGain * a, a);
}
`;

export const QUAD_VERT = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

/**
 * Feedback: this frame's scene composited over the previous frame, zoomed,
 * rotated and warped a little. The whole trail effect lives in these lines.
 */
export const FEEDBACK_FRAG = /* glsl */ `
precision highp float;
varying vec2 vUv;
uniform sampler2D uScene;
uniform sampler2D uPrev;
uniform float uDecay;
uniform float uZoom;
uniform float uRotate;
uniform float uWarp;
uniform float uTime;
uniform float uAspect;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

// Two octaves of value noise, enough to bend the trail without shimmering.
float vnoise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y) * 2.0 - 1.0;
}

void main() {
  vec2 p = vUv - 0.5;
  p.x *= uAspect;

  float s = sin(uRotate), c = cos(uRotate);
  p = mat2(c, -s, s, c) * p;
  p /= max(0.0001, uZoom);

  if (uWarp > 0.0) {
    // Warp offsets are in UV, so the deformation is the same shape at any size.
    vec2 q = p * 2.2;
    vec2 w = vec2(vnoise(q + vec2(0.0, uTime * 0.13)),
                  vnoise(q + vec2(5.7, uTime * 0.11 + 3.1)));
    p += w * uWarp * 0.02;
  }

  p.x /= uAspect;
  vec2 prevUv = p + 0.5;

  vec3 prev = vec3(0.0);
  if (prevUv.x > 0.0 && prevUv.x < 1.0 && prevUv.y > 0.0 && prevUv.y < 1.0) {
    prev = texture2D(uPrev, prevUv).rgb * uDecay;
  }

  vec3 scene = texture2D(uScene, vUv).rgb;
  gl_FragColor = vec4(scene + prev, 1.0);
}
`;

export const THRESHOLD_FRAG = /* glsl */ `
precision highp float;
varying vec2 vUv;
uniform sampler2D uTex;
uniform float uThreshold;
uniform float uSoft;

void main() {
  vec3 c = texture2D(uTex, vUv).rgb;
  float l = dot(c, vec3(0.2126, 0.7152, 0.0722));
  float k = smoothstep(uThreshold, uThreshold + uSoft, l);
  gl_FragColor = vec4(c * k, 1.0);
}
`;

/** Separable gaussian. uTexel is 1/size of THIS mip, so the blur stays
 *  proportional to the frame rather than fixed in pixels. */
export const BLUR_FRAG = /* glsl */ `
precision highp float;
varying vec2 vUv;
uniform sampler2D uTex;
uniform vec2 uTexel;
uniform vec2 uDir;
uniform float uRadius;

void main() {
  vec2 o = uTexel * uDir * uRadius;
  vec3 sum = texture2D(uTex, vUv).rgb * 0.227027;
  sum += texture2D(uTex, vUv + o * 1.3846).rgb * 0.316216;
  sum += texture2D(uTex, vUv - o * 1.3846).rgb * 0.316216;
  sum += texture2D(uTex, vUv + o * 3.2308).rgb * 0.070270;
  sum += texture2D(uTex, vUv - o * 3.2308).rgb * 0.070270;
  gl_FragColor = vec4(sum, 1.0);
}
`;

export const COMBINE_FRAG = /* glsl */ `
precision highp float;
varying vec2 vUv;
uniform sampler2D uBase;
uniform sampler2D uBloom0;
uniform sampler2D uBloom1;
uniform sampler2D uBloom2;
uniform float uBloom;
uniform float uOpacity;

void main() {
  vec3 base = texture2D(uBase, vUv).rgb;
  vec3 b = texture2D(uBloom0, vUv).rgb * 0.5
         + texture2D(uBloom1, vUv).rgb * 0.32
         + texture2D(uBloom2, vUv).rgb * 0.18;
  gl_FragColor = vec4((base + b * uBloom) * uOpacity, 1.0);
}
`;

/** Exposure, filmic curve, vignette and a grain whose grid is fixed in
 *  1080p-equivalent cells so it does not change density with resolution. */
export const FINAL_FRAG = /* glsl */ `
precision highp float;
varying vec2 vUv;
uniform sampler2D uTex;
uniform vec3 uBackground;
uniform float uExposure;
uniform float uVignette;
uniform float uGrain;
uniform float uFrame;
uniform float uAspect;

float hash13(vec3 p) {
  p = fract(p * 0.1031);
  p += dot(p, p.yzx + 33.33);
  return fract((p.x + p.y) * p.z);
}

vec3 filmic(vec3 x) {
  // ACES-style approximation: keeps highlights from clipping to flat white.
  const float a = 2.51, b = 0.03, c = 2.43, d = 0.59, e = 0.14;
  return clamp((x * (a * x + b)) / (x * (c * x + d) + e), 0.0, 1.0);
}

void main() {
  vec3 col = texture2D(uTex, vUv).rgb * uExposure;
  col += uBackground;
  col = filmic(col);

  if (uVignette > 0.0) {
    vec2 p = (vUv - 0.5) * vec2(uAspect, 1.0);
    float v = 1.0 - uVignette * dot(p, p) * 1.2;
    col *= clamp(v, 0.0, 1.0);
  }

  if (uGrain > 0.0) {
    vec2 cell = floor(vec2(vUv.x * 1920.0, vUv.y * 1080.0));
    float n = hash13(vec3(cell, uFrame)) - 0.5;
    col += n * uGrain;
  }

  gl_FragColor = vec4(col, 1.0);
}
`;

/** Stereo pass: anaglyph, side-by-side, or wiggle.
 *  uTex: the graded image
 *  uStrength: strength of the effect (0.0 to 1.0)
 *  uMode: 1 = anaglyph, 2 = side-by-side, 3 = wiggle
 *  uFrame: frame number (for wiggle)
 *  uAspect: aspect ratio of the viewport
 */
export const STEREO_FRAG = /* glsl */ `
precision highp float;
varying vec2 vUv;
uniform sampler2D uTex;
uniform float uStrength;
uniform int uMode;
uniform float uFrame;
uniform float uAspect;

void main() {
  vec2 uv = vUv;
  float strength = uStrength * 0.05; // scale to reasonable shift
  float offset = 0.0;

  if (uMode == 3) { // wiggle
    offset = sin(uFrame * 0.1) * strength * 2.0; // oscillate
  } else {
    offset = strength; // fixed offset for anaglyph and sbs
  }

  vec3 col;
  if (uMode == 1) { // anaglyph: red/cyan
    // left eye: red channel shifted left, right eye: green+blue shifted right
    vec2 uvL = uv + vec2(-offset / uAspect, 0.0);
    vec2 uvR = uv + vec2(offset / uAspect, 0.0);
    float r = texture2D(uTex, uvL).r;
    float g = texture2D(uTex, uvR).g;
    float b = texture2D(uTex, uvR).b;
    col = vec3(r, g, b);
  } else if (uMode == 2) { // side-by-side
    // squeeze half width and place left and right
    if (uv.x < 0.5) {
      // left eye: left half
      vec2 uvS = vec2(uv.x * 2.0, uv.y);
      col = texture2D(uTex, uvS).rgb;
    } else {
      // right eye: right half
      vec2 uvS = vec2((uv.x - 0.5) * 2.0, uv.y);
      col = texture2D(uTex, uvS).rgb;
    }
  } else {
    // fallback: just copy
    col = texture2D(uTex, uv).rgb;
  }

  gl_FragColor = vec4(col, 1.0);
}
`;
